import { BAND_COLORS } from "../bands";

// BVAS v3 — Birmingham Vasculitis Activity Score, version 3.
// Score only items attributable to ACTIVE vasculitis. A global toggle chooses
// whether all ticked items are scored as "persistent" (active but not new/worse
// in the prior 4 weeks) or "new/worse". Per BVAS v3, if ANY item is new/worse,
// everything is scored as new/worse.
//
// ⚠️ The per-system MAXIMA and caps below are authoritative (persistent total 33,
// new/worse total 63; source: BVAS v3 scoring). The individual MINOR-item point
// values are approximated (minor = 1 persistent / 2 new-worse; major ♦ = system
// max), because the exact per-item weight table is not reproduced here. Verify
// against the published BVAS v3 glossary before clinical use.
// ⚠️ This order is a persistence contract: field keys are positional (`s<section>_<item>`)
// and saved scores store them, so reordering or removing an item silently rehydrates
// old visits onto the wrong items. Only append.
const SECTIONS = [
    {
        name: "General",
        maxP: 2,
        maxN: 3,
        items: [
            "Myalgia",
            "Arthralgia / arthritis",
            "Fever ≥38°C (≥100.4°F)",
            "Weight loss ≥2 kg",
        ],
    },
    {
        name: "Cutaneous",
        maxP: 3,
        maxN: 6,
        items: ["Infarct", "Purpura", "Ulcer", "♦ Gangrene", "Other skin vasculitis"],
    },
    {
        name: "Mucous membranes / eyes",
        maxP: 3,
        maxN: 6,
        items: [
            "Mouth ulcers",
            "Genital ulcers",
            "Adnexal inflammation",
            "Significant proptosis",
            "Scleritis / Episcleritis",
            "Conjunctivitis / Blepharitis / Keratitis",
            "Blurred vision",
            "Uveitis",
            "♦ Retinal changes (vasculitis / thrombosis / exudate / haemorrhage)",
        ],
    },
    {
        name: "ENT",
        maxP: 3,
        maxN: 6,
        items: [
            "Bloody nasal discharge / crusts / ulcers / granulomata",
            "Paranasal sinus involvement",
            "Subglottic stenosis",
            "Conductive hearing loss",
            "♦ Sensorineural hearing loss",
        ],
    },
    {
        name: "Chest",
        maxP: 3,
        maxN: 6,
        items: [
            "Wheeze",
            "Pleural effusion / pleurisy",
            "Infiltrate",
            "Endobronchial involvement",
            "♦ Massive haemoptysis / alveolar haemorrhage",
            "♦ Respiratory failure",
        ],
    },
    {
        name: "Cardiovascular",
        maxP: 3,
        maxN: 6,
        items: [
            "Loss of pulses",
            "Valvular heart disease",
            "Pericarditis",
            "♦ Ischaemic cardiac pain",
            "♦ Cardiomyopathy",
            "♦ Congestive cardiac failure",
        ],
    },
    {
        name: "Abdominal",
        maxP: 4,
        maxN: 9,
        items: ["Peritonitis", "Bloody diarrhoea", "♦ Ischaemic abdominal pain"],
    },
    {
        name: "Renal",
        maxP: 6,
        maxN: 12,
        items: ["Hypertension", "Proteinuria >1+", "♦ Haematuria ≥10 RBCs/hpf"],
    },
    {
        name: "Nervous system",
        maxP: 6,
        maxN: 9,
        items: [
            "Headache",
            "Meningitis",
            "Organic confusion",
            "Seizures (not hypertensive)",
            "♦ Cerebrovascular accident",
            "♦ Spinal cord lesion",
            "♦ Cranial nerve palsy",
            "Sensory peripheral neuropathy",
            "♦ Mononeuritis multiplex",
        ],
    },
];

// Give each item a stable key and a major flag (major = "♦" prefix).
SECTIONS.forEach((sec, si) => {
    sec.items = sec.items.map((label, ii) => ({
        key: `s${si}_${ii}`,
        label,
        major: label.startsWith("♦"),
    }));
});

const FIELDS = [
    {
        key: "firstAssessment",
        type: "toggle",
        section: "Patient",
        label: "Is this the patient's first assessment?",
        default: 0,
    },
    ...SECTIONS.flatMap((sec) =>
        sec.items.map((it) => ({
            key: it.key,
            type: "toggle",
            section: sec.name,
            label: it.label,
            default: 0,
        }))
    ),
];

export const bvas = {
    id: "bvas",
    name: "BVAS (version 3)",
    subtitle: "Birmingham vasculitis activity score",
    group: "Vasculitis",
    status: "ready",
    layout: "compact",
    options: [
        { key: "mode", label: "All abnormalities", choices: ["Persistent", "New/Worse"], default: "Persistent" },
    ],
    fields: FIELDS,
    compute: (v, o) => {
        const newWorse = o.mode === "New/Worse";
        let total = 0;
        SECTIONS.forEach((sec) => {
            let sys = 0;
            sec.items.forEach((it) => {
                if (v[it.key]) {
                    const w = newWorse
                        ? it.major
                            ? sec.maxN
                            : 2
                        : it.major
                            ? sec.maxP
                            : 1;
                    sys += w;
                }
            });
            total += Math.min(sys, newWorse ? sec.maxN : sec.maxP);
        });
        return total;
    },
    // BVAS is an activity score without validated severity bands.
    interpret: (score) => ({
        label: score === 0 ? "No active vasculitis" : "Active vasculitis",
        color: score === 0 ? BAND_COLORS.remission : BAND_COLORS.high,
    }),
    about:
        "The Birmingham Vasculitis Activity Score version 3 (BVAS v3) records features of active vasculitis. Score an item only if attributable to active vasculitis. If all abnormalities are persistent disease (active vasculitis not new or worse in the prior 4 weeks) select 'Persistent'; if any item is new or worse, all items are scored as new/worse. Maximum persistent score is 33 and maximum new/worse score is 63. ♦ indicates a major item.",
    references: [
        "Mukhtyar C, et al. Modification and validation of the Birmingham Vasculitis Activity Score (version 3). Ann Rheum Dis. 2009.",
    ],
};
